import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { ChevronDown, Mail } from 'lucide-react';

// Where a candidate was found, in the user's words. The backend's `source_type` vocabulary
// is machine-facing (website | search_snippet | extracted_page | knowledge_graph |
// local_pack | ai_overview); this is the only place it becomes something to read.
const SOURCE_LABELS = {
  website: 'Merchant site',
  search_snippet: 'Web research',
  extracted_page: 'Web research',
  knowledge_graph: 'Google panel',
  local_pack: 'Google listing',
  ai_overview: 'AI Overview',
};

// A contact page is worth naming on its own: it is the strongest thing a merchant can say
// about where to reach it, and it is what makes one row obviously better than another.
const CONTEXT_LABELS = {
  contact_page: 'Contact page',
  legal_page: 'Legal page',
  about_page: 'About page',
  homepage: 'Homepage',
  footer: 'Footer',
};

export const candidateProvenance = (candidate) => {
  const context = CONTEXT_LABELS[candidate?.source_context];
  const source = SOURCE_LABELS[candidate?.source_type];
  // The page beats the channel when we have both: "Contact page" tells a salesperson more
  // than "Merchant site" does. Neither known is possible on legacy records, so it degrades
  // to nothing rather than to a guess.
  if (context && source) return `${source} · ${context}`;
  return context || source || '';
};

/**
 * The candidate list for one contact field.
 *
 * Renders NOTHING below two candidates. That is deliberate: with zero or one there is
 * nothing to choose between, and a control that opens a list of one is noise. The field
 * then looks exactly as it did before this existed.
 *
 * TWO INDEPENDENT THINGS live in this list, and the whole point is that they never merge:
 *
 *   TICKING is for composing a message. Zero, one or many, and it never writes back to the
 *   field — the address CiaoLead stores is not decided by who you want to email today.
 *
 *   THE PRIMARY is the field itself: the one address saved as contact_email, and therefore
 *   the one Salesforce is later given. At most one, and only where the caller asks for it
 *   by passing `onPrimaryChange` — a field with no notion of a primary (the phones) gets
 *   exactly the list it had before, with no extra control rendered.
 *
 * `primaryValue` is READ from the field, never held here: this component shows which
 * candidate the field currently holds and offers to change it. Choosing one does not save
 * anything; it edits the form like typing would, and the user still has to Save.
 */
export const CandidatePicker = ({
  candidates, selected, onToggle, testId, label, primaryValue, onPrimaryChange,
}) => {
  if (!Array.isArray(candidates) || candidates.length < 2) return null;

  const count = selected.length;
  // The field's own value decides what looks primary — not what is ticked, and not any
  // state of our own. The comparison is exact against the field with its surrounding
  // whitespace ignored, so an address typed by hand shows as primary when it IS one of
  // these addresses (spacing aside) and otherwise matches nothing: a different address, or
  // the same one in a different case, leaves every row offering to become primary.
  const primary = (primaryValue || '').trim();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200 transition-colors"
          data-testid={testId}
        >
          {count} of {candidates.length}
          <ChevronDown className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-2">
        <p className="px-2 pb-2 text-xs text-slate-400">{label}</p>
        <div className="max-h-64 space-y-1 overflow-y-auto">
          {candidates.map((candidate) => {
            const value = candidate.value;
            const provenance = candidateProvenance(candidate);
            const isPrimary = Boolean(onPrimaryChange) && value === primary;
            return (
              // The row is a plain container and only the checkbox and its text are inside
              // the label, so the primary control cannot tick the box by being clicked.
              <div
                key={value}
                className="flex items-start gap-2 rounded px-2 py-1.5 hover:bg-white/5"
              >
                <label className="flex min-w-0 flex-1 cursor-pointer items-start gap-2">
                  <Checkbox
                    checked={selected.includes(value)}
                    onCheckedChange={() => onToggle(value)}
                    className="mt-0.5"
                    data-testid={`${testId}-option`}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-slate-100">{value}</span>
                    {provenance && (
                      <span className="block text-xs text-slate-500">{provenance}</span>
                    )}
                  </span>
                </label>
                {onPrimaryChange && (isPrimary ? (
                  <span
                    className="mt-0.5 shrink-0 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-300"
                    data-testid={`${testId}-primary`}
                  >
                    Primary
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => onPrimaryChange(value)}
                    className="mt-0.5 shrink-0 rounded-full border border-white/15 px-2 py-0.5 text-xs text-slate-400 hover:border-white/30 hover:text-slate-100"
                    data-testid={`${testId}-set-primary`}
                    title={`Use ${value} as the saved contact`}
                  >
                    Set as primary
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

/**
 * Opens the user's own mail client with every ticked address in the recipients line.
 *
 * A plain `mailto:` and nothing more — no subject, no body, no template, no queue. Nothing
 * is stored and no mail account is connected: the user writes and sends in their own client.
 * Disabled with nothing ticked, because an empty recipients line is not a message.
 */
export const MailtoButton = ({ addresses, testId }) => {
  const recipients = addresses.filter(Boolean);
  const disabled = recipients.length === 0;
  // `encodeURI` over the whole href, never `encodeURIComponent` per address: the latter
  // turns `@` into `%40`, which RFC 6068 permits but many mail clients render badly. This
  // leaves `@ . ,` intact and escapes only what genuinely must be.
  const href = disabled ? undefined : encodeURI(`mailto:${recipients.join(',')}`);

  return (
    <Button
      asChild={!disabled}
      type="button"
      variant="ghost"
      size="sm"
      disabled={disabled}
      className="h-6 gap-1 px-2 text-xs text-slate-400 hover:text-slate-100"
      data-testid={testId}
      title={disabled ? 'Tick at least one address' : `Email ${recipients.length} recipient(s)`}
    >
      {disabled ? (
        <span className="flex items-center gap-1">
          <Mail className="h-3 w-3" />
          Email
        </span>
      ) : (
        <a href={href}>
          <Mail className="h-3 w-3" />
          Email
        </a>
      )}
    </Button>
  );
};
